import React, { useEffect, useState } from "react";
import axiosClient from "../../../config/axios.js";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import Spinner from "react-bootstrap/Spinner";
import { Panel, PanelHeader, PanelBody } from "../../../components/panel/panel";
import OrderVialForm from "../../../forms/OrderVialForm.js";
import { useFormik } from "formik";
import * as Yup from "yup"; // Asegúrate de importar Yup
import Swal from "sweetalert2";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import "moment/locale/es"; 

function OrdersVial() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const showAlert = params.get("showAlert");

    if (showAlert === "true") {
      Swal.fire({
        title: "¿Estás seguro?",
        text: "Estás entrando a la sección de Varo Express",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, entrar",
        cancelButtonText: "No, regresar al inicio",
      }).then((result) => {
        if (!result.isConfirmed) {
          navigate("/");
        }
      });
    }
  }, [location.search, navigate]);

  const [data, setData] = useState([]);
  const [records, setRecords] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("folio");
  const [sortDirection, setSortDirection] = useState("desc");
  const [serviceTypes, setServiceTypes] = useState([]);

  const formik = useFormik({
    initialValues: {
      start: "",
      end: "",
      status: "all",
      statusOrden: "all",
      typeServiceId: "all"
    },
    validationSchema: Yup.object().shape({
      start: Yup.date()
        .nullable()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .max(
          Yup.ref("end"),
          "La fecha de inicio no puede ser mayor que la fecha de fin"
        ),
      end: Yup.date()
        .nullable()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .min(
          Yup.ref("start"),
          "La fecha de fin no puede ser menor que la fecha de inicio"
        ),
      status: Yup.string().required("Campo requerido"),
      statusOrden: Yup.string().required("Campo requerido"), 
    }),
    onSubmit: () => {
      setCurrentPage(1);
      setLoading(true);
      fetchData(1, searchValue, sortField, sortDirection);
    },
  });

  const fetchData = async (page = 1, search = "", sortField, sortDirection) => {
  try {
    const response = await axiosClient.get(`/order-vial/datatable`, {
      params: {
        draw: 1,
        start: (page - 1) * rowsPerPage,
        length: rowsPerPage,
        search: search,
        dateFrom: formik.values.start || undefined,
        dateTo: formik.values.end || undefined,
        status:
          formik.values.status !== "all" ? formik.values.status : undefined,
        statusOrden:
          formik.values.statusOrden !== "all" ? formik.values.statusOrden: undefined,
        typeServiceId:
          formik.values.typeServiceId !== "all"
            ? formik.values.typeServiceId
            : undefined,
        sort: sortField,
        dir: sortDirection,
      },
    });

    const vialData = response.data.data || [];

    // `records` (datos normalizados)
    const newRecords = [];

    vialData.forEach((vialOrder) => {
      const operador = vialOrder.operator;

      // Objeto para datos normalizados (records)
      newRecords.push({
        order_id: vialOrder.id,
        user_id: operador?.id,
        fecha_hora_reporte: vialOrder.fecha_hora_orden,
        folio: vialOrder.folio,
        grua: vialOrder.grua,
        subtotal: (vialOrder.total / 1.16).toFixed(2),
        totalConIVA:vialOrder.total,
        totalPagos: vialOrder.totalPagos || 0,
        totalFacturas: vialOrder.totalFacturas || 0,
        tipoOrden: "ordenVial",
        tipo: "Varo Express",
        razon: `Varo Express: ${vialOrder?.typeService?.type_service_name || ""}`,
        operador: operador,
        tipoServicio: vialOrder?.typeService?.type_service_name,
        Crane: vialOrder.Crane,
      });
    });

    setData(response.data);
    setRecords(newRecords);
    setLoading(false);
  } catch (error) {
    console.error(error);
    setLoading(false);
  }
};

  useEffect(() => {
    setLoading(true);
    fetchData(currentPage, searchValue, sortField, sortDirection);
  }, [currentPage, rowsPerPage, sortField, sortDirection]);

  useEffect(() => {
    const checkAndFetch = async () => {
      const isValid = await formik.validateForm().then((errors) => {
        return Object.keys(errors).length === 0;
      });

      const hasFilters =
        formik.values.start || formik.values.end || formik.values.status || formik.values.statusOrden || formik.values.typeServiceId;;

      if (isValid && hasFilters) {
        setCurrentPage(1);
        setLoading(true);
        fetchData(1, searchValue, sortField, sortDirection);
      }
    };

    const timeout = setTimeout(() => {
      checkAndFetch();
    }, 300);

    return () => clearTimeout(timeout);
  }, [formik.values.start, formik.values.end, formik.values.status, formik.values.statusOrden, formik.values.typeServiceId, ]);

  
  useEffect(() => {
    const fetchServiceTypes = async () => {
      try {
        const response = await axiosClient.get("/typeServices/datatable");
        setServiceTypes(response.data.data); // acceso al array de tipos
      } catch (error) {
        console.error("Error al cargar tipos de servicio", error);
      }
    };

    fetchServiceTypes();
  }, []);

  const columns = [
    {
      name: "Folio",
      selector: (row) => (
        <Link to={`/orders-vial/editOrder/${row.id}`}>{row.folio}</Link>
      ),
      sortable: true,
      sortField: "folio",
    },
    {
      name: "Fecha y Hora",
      selector: (row) => moment(row.fecha_hora_orden).format("DD/MM/YYYY HH:mm"),
      sortable: true,
      sortField: "fecha_hora_orden",
    },
    {
      name: "Estatus",
      selector: (row) => row.estatus,
      sortable: true,
      sortField: "estatus",
    },
    {
      name: "Tipo de servicio",
      selector: (row) => row.typeService.type_service_name,
      sortable: false,
      grow: 2,
    },
    {
      name: "Vehículo",
      selector: (row) =>
        row.vehicles?.[0]?.VehicleModel?.model_name || "Sin asignar",
      sortable: false,
    },
    {
      name: "Placa",
      selector: (row) => row.vehicles?.[0]?.placas || "Sin placa",
      sortable: false,
    },
    {
      name: "Operador",
      selector: (row) =>
        `${row.operator?.name || ""} ${row.operator?.first_surname || ""}`,
      sortable: false,
    },
    {
      name: "Chofer auxiliar",
      selector: (row) =>
        `${row.chofer_auxiliar?.name || ""} ${row.chofer_auxiliar?.first_surname || ""}`,
      sortable: false,
    },
  ];

  const customStyles = {
    headCells: {
      style: {
        color: "#202124",
        fontSize: "14px",
        backgroundColor: "#EEEEEE",
        border: "none",
      },
    },
  };

  const paginationComponentOptions = {
    rowsPerPageText: "Filas por página",
    rangeSeparatorText: "de",
    selectAllRowsItem: false,
    selectAllRowsItemText: "Todos",
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      setCurrentPage(1);
      setLoading(true);
      fetchData(1, searchValue, sortField, sortDirection);
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Inicio</Link>
            </li>
            <li className="breadcrumb-item active">Órdenes Viales</li>
          </ol>
          <h1 className="page-header mb-0">Varo Express</h1>
        </div>
        <div className="ms-auto">
          <Link
            to={"/orders-vial/newOrder"}
            className="btn btn-success btn-rounded px-4 rounded-pill"
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-success-900"></i>{" "}
            Nueva orden vial
          </Link>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-xl-12">
          <Panel>
            <PanelHeader>Listado de órdenes</PanelHeader>
            <PanelBody>
              <div className="container-fluid">
                <OrderVialForm
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  onSearch={() => formik.handleSubmit()}
                  formik={formik}
                  serviceTypes={serviceTypes}
                  records={records}
                />
              </div>
              <DataTable
                columns={columns}
                data={data.data}
                progressPending={loading}
                sortServer
                pagination
                paginationServer
                paginationPerPage={rowsPerPage}
                paginationRowsPerPageOptions={[100,200,300,400,500]}
                paginationTotalRows={data.recordsTotal}
                paginationDefaultPage={currentPage}
                subHeader
                customStyles={customStyles}
                pointerOnHover
                paginationComponentOptions={paginationComponentOptions}
                noDataComponent="Sin información por mostrar"
                progressComponent={
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                }
                subHeaderComponent={
                  <div className="row">
                    <div className="col-sm-12 col-md-12">
                      <div className="input-group mb-3">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Buscar..."
                          value={searchValue}
                          onChange={(e) => setSearchValue(e.target.value)}
                          onKeyPress={handleKeyPress}
                        />
                        <div
                          className="input-group-text btn btn-info"
                          onClick={() => {
                            setLoading(true);
                            setCurrentPage(1);
                            formik.handleSubmit();
                            fetchData(
                              currentPage,
                              searchValue,
                              sortField,
                              sortDirection
                            ); // Realizar búsqueda aquí
                          }}
                        >
                          <i className="fa fa-search"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                }
                onChangePage={(page) => setCurrentPage(page)}
                onChangeRowsPerPage={(newPerPage) => {
                  setRowsPerPage(newPerPage);
                  setCurrentPage(1);
                }}
                onSort={(column, sortDirection) => {
                  setSortDirection(sortDirection);
                  setSortField(column.sortField);
                  setCurrentPage(1);
                }}
              />
            </PanelBody>
          </Panel>
        </div>
      </div>
    </div>
  );
}

export default OrdersVial;
